import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAlertRules, useChannels, useCreateAlertRule, useUpdateAlertRule } from 'src/api/queries';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Button } from 'src/components/ui/Button';
import { Icon } from 'src/components/ui/Icon';
import { Notice } from 'src/components/ui/Notice';
import { Skeleton } from 'src/components/ui/Skeleton';
import { ALERT_SCOPE_ORDER, ALERT_SURFACE_LABEL, ALERT_SURFACE_MEANING, formatAlertScopes } from 'src/lib/alerts';
import { ACTION_THRESHOLD } from 'src/lib/relevance';
import { formatDate } from 'src/lib/time';
import type { EditorialAlertRule, EditorialAlertSurface } from 'src/types/editorial';

const COOLDOWN_PRESETS = [1, 3, 6, 10, 15] as const;

/** Named tiers over a raw number — "65" alone doesn't say whether that's strict or loose. */
const RELEVANCE_PRESETS = [
  { value: '', label: 'Cualquier relevancia · Sin umbral' },
  { value: '50', label: '50 · Más sensible' },
  {
    value: String(ACTION_THRESHOLD),
    label: `${ACTION_THRESHOLD} · Recomendado`,
  },
  { value: '80', label: '80 · Solo alta prioridad' },
] as const;
const RELEVANCE_PRESET_VALUES: ReadonlyArray<string> = RELEVANCE_PRESETS.map(preset => preset.value);

/**
 * Create and edit share this page — same fields, same right-rail summary, because a second form
 * would be a second place for the surfaces list to drift. Reached from `/alertas` and from a
 * rule's "Editar" action; `ruleId` in the URL is what tells the two apart.
 *
 * The rule itself isn't its own fetch: the list is short and already loaded by the Alertas screen
 * (`useAlertRules` is one React Query cache entry), so opening this page from there is instant and
 * a direct link/refresh just waits on the same query everyone else does.
 */
export function RuleFormWorkspace() {
  const { ruleId } = useParams<{ ruleId: string }>();
  const rules = useAlertRules(true);

  if (ruleId) {
    if (rules.isPending) {
      return (
        <div className="page-medium">
          <Skeleton variant="rows" count={4} />
        </div>
      );
    }
    if (rules.isError) {
      return (
        <div className="page-medium">
          <LoadFailure title="No se pudo cargar la regla" error={rules.error} onRetry={rules.refetch} />
        </div>
      );
    }
    const rule = rules.data.rules.find(item => item.ruleId === ruleId);
    if (!rule) {
      return (
        <div className="page-medium">
          <Notice tone="unresolved" title="Esta regla ya no existe">
            Puede que se haya borrado desde otra sesión.
          </Notice>
        </div>
      );
    }
    return <RuleForm rule={rule} />;
  }

  return <RuleForm />;
}

type FormState = {
  name: string;
  keywords: Array<string>;
  matchAll: boolean;
  /** The three real ámbitos only — `relevance` is never in here, it rides along with `prenota`. */
  surfaces: Array<EditorialAlertSurface>;
  channelIds: Array<string>;
  minRelevanceScore: string;
  relevanceCustom: boolean;
  notifyEmails: Array<string>;
  cooldownMinutes: string;
  cooldownCustom: boolean;
};

function initialState(rule: EditorialAlertRule | undefined): FormState {
  const cooldown = rule?.cooldownMinutes ?? 15;
  const minRelevanceScore = rule?.minRelevanceScore ?? null;
  return {
    name: rule?.name ?? '',
    keywords: rule?.keywords ?? [],
    matchAll: rule?.matchAll ?? false,
    surfaces: (rule?.surfaces ?? [...ALERT_SCOPE_ORDER]).filter(surface => surface !== 'relevance'),
    channelIds: rule?.channelIds ?? [],
    minRelevanceScore: minRelevanceScore === null ? '' : String(minRelevanceScore),
    relevanceCustom: minRelevanceScore !== null && !RELEVANCE_PRESET_VALUES.includes(String(minRelevanceScore)),
    notifyEmails: rule?.notifyEmails ?? [],
    cooldownMinutes: String(cooldown),
    cooldownCustom: !(COOLDOWN_PRESETS as ReadonlyArray<number>).includes(cooldown),
  };
}

function RuleForm({ rule }: { rule?: EditorialAlertRule }) {
  const navigate = useNavigate();
  const channels = useChannels();
  const create = useCreateAlertRule();
  const update = useUpdateAlertRule();

  const [form, setForm] = useState<FormState>(() => initialState(rule));

  const canSave = form.name.trim().length > 0 && form.keywords.length > 0 && form.surfaces.length > 0;
  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const cooldownMinutes = Number(form.cooldownMinutes) || 0;

  const watchesRelevance = form.surfaces.includes('prenota');

  function save() {
    const payload = {
      name: form.name.trim(),
      keywords: form.keywords,
      matchAll: form.matchAll,
      surfaces: watchesRelevance ? [...form.surfaces, 'relevance' as const] : form.surfaces,
      channelIds: form.channelIds,
      minRelevanceScore: !watchesRelevance || form.minRelevanceScore.trim() === '' ? null : Number(form.minRelevanceScore),
      notifyEmails: form.notifyEmails,
      cooldownMinutes,
    };

    const mutation = rule ? update.mutateAsync({ ruleId: rule.ruleId, ...payload }) : create.mutateAsync(payload);
    void mutation.then(() => navigate('/alertas', { replace: true })).catch(() => undefined);
  }

  return (
    <div className="page-hero rule-form-page">
      <Link className="page-back" to="/alertas">
        ← Volver a alertas
      </Link>

      <div className="rule-form-page__layout">
        <div>
          <header className="landmark">
            <p className="thread__eyebrow">Centro de monitoreo</p>
            <h1 className="section-title">{rule ? 'Editar regla' : 'Nueva regla'}</h1>
            <p className="relevance__note">
              {rule ? 'Actualiza esta regla con el mismo nivel de detalle con el que fue creada.' : 'Define las palabras o frases a detectar y en dónde el pipeline debe buscarlas.'}
            </p>
          </header>

          {error && (
            <Notice tone="contradicted" title="No se pudo guardar la regla" role="alert">
              Revisa los campos e inténtalo otra vez.
            </Notice>
          )}

          <div className="rule-form__panel">
            <section className="rule-form__block">
              <div className="rule-form__blockHead">
                <span className="rule-form__number">01</span>
                <div>
                  <h2 className="section-title">Define la alerta</h2>
                  <p className="relevance__note">Asigna un nombre y agrega los términos que debe detectar.</p>
                </div>
              </div>

              <label className="rule-form__field">
                <span>Nombre de la alerta</span>
                <input value={form.name} maxLength={120} onChange={event => setForm({ ...form, name: event.target.value })} placeholder="Huachicol en Pemex" />
              </label>

              <div className="rule-form__field">
                <span>Palabras o frases a detectar</span>
                <ChipInput values={form.keywords} onChange={keywords => setForm({ ...form, keywords })} placeholder="Escribe una palabra o frase" />
                <small>Presiona coma o Enter para agregar cada término. Se comparan palabras completas, sin acentos ni mayúsculas — el plural hay que escribirlo.</small>
              </div>

              <label className="rule-form__check">
                <input type="checkbox" checked={form.matchAll} onChange={event => setForm({ ...form, matchAll: event.target.checked })} />
                <span>
                  <strong>Exigir que todos los términos coincidan en el mismo resultado</strong>
                  <small>La alerta solo se activa cuando todos los términos aparecen juntos en un mismo resultado.</small>
                </span>
              </label>

              <div className="rule-form__field">
                <span>Fuentes de captura</span>
                <small>Selecciona una o más fuentes donde Pressentia debe monitorear esta alerta. Sin ninguna marcada, la regla aplica a todas.</small>
                <div className="choice-grid">
                  {channels.data?.channels.map(channel => (
                    <label key={channel.channelId} className="choice choice--compact" data-checked={form.channelIds.includes(channel.channelId)}>
                      <input
                        type="checkbox"
                        checked={form.channelIds.includes(channel.channelId)}
                        onChange={event =>
                          setForm({
                            ...form,
                            channelIds: event.target.checked ? [...form.channelIds, channel.channelId] : form.channelIds.filter(item => item !== channel.channelId),
                          })
                        }
                      />
                      <span className="choice__tick" aria-hidden="true" />
                      <strong>{channel.name || channel.channelId}</strong>
                      {/* Not per-channel data — every source this pipeline captures is a YouTube live
                        stream, so this is a domain constant rather than a `channel` field. */}
                      <span className="choice__source-type">
                        <Icon name="broadcasts" />
                        YouTube Live
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </section>

            <section className="rule-form__block">
              <div className="rule-form__blockHead">
                <span className="rule-form__number">02</span>
                <div>
                  <h2 className="section-title">Dónde buscar</h2>
                  <p className="relevance__note">Elige en qué parte del pipeline debe buscar.</p>
                </div>
              </div>

              <div className="choice-grid">
                {ALERT_SCOPE_ORDER.map(surface => (
                  <label key={surface} className="choice" data-checked={form.surfaces.includes(surface)}>
                    <input
                      type="checkbox"
                      checked={form.surfaces.includes(surface)}
                      onChange={event =>
                        setForm({
                          ...form,
                          surfaces: event.target.checked ? [...form.surfaces, surface] : form.surfaces.filter(item => item !== surface),
                        })
                      }
                    />
                    <span className="choice__tick" aria-hidden="true" />
                    <span>
                      <strong>{ALERT_SURFACE_LABEL[surface]}</strong>
                      <small>{ALERT_SURFACE_MEANING[surface]}</small>
                    </span>
                  </label>
                ))}
              </div>

              {/* Relevancia is a dependent setting of Historias, never its own ámbito — it appears
                paired with Silencio because both are the same kind of decision (a threshold on
                when to interrupt), and Settings Cards must start on the same Y axis. */}
              <div className="settings-grid">
                {watchesRelevance && (
                  <div className="setting-card">
                    <div className="setting-card__head">
                      <span className="setting-card__icon" aria-hidden="true">
                        <Icon name="tune" />
                      </span>
                      <div>
                        <strong>Relevancia</strong>
                        <small>Solo aparece si Historias está seleccionado.</small>
                      </div>
                    </div>
                    <label className="rule-form__field">
                      <span>Nivel de relevancia</span>
                      {form.relevanceCustom ? (
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={form.minRelevanceScore}
                          onChange={event =>
                            setForm({
                              ...form,
                              minRelevanceScore: event.target.value,
                            })
                          }
                        />
                      ) : (
                        <select
                          value={form.minRelevanceScore}
                          onChange={event =>
                            event.target.value === 'custom'
                              ? setForm({
                                  ...form,
                                  relevanceCustom: true,
                                  minRelevanceScore: String(ACTION_THRESHOLD),
                                })
                              : setForm({
                                  ...form,
                                  minRelevanceScore: event.target.value,
                                })
                          }
                        >
                          {RELEVANCE_PRESETS.map(preset => (
                            <option key={preset.value} value={preset.value}>
                              {preset.label}
                            </option>
                          ))}
                          <option value="custom">Personalizado…</option>
                        </select>
                      )}
                    </label>
                  </div>
                )}

                <div className="setting-card">
                  <div className="setting-card__head">
                    <span className="setting-card__icon" aria-hidden="true">
                      <Icon name="alerts" />
                    </span>
                    <div>
                      <strong>Silencio entre avisos</strong>
                      <small>Tiempo mínimo antes de volver a notificar.</small>
                    </div>
                  </div>
                  <label className="rule-form__field">
                    <span>Tiempo de silencio</span>
                    {form.cooldownCustom ? (
                      <input
                        type="number"
                        min={0}
                        max={1440}
                        value={form.cooldownMinutes}
                        onChange={event =>
                          setForm({
                            ...form,
                            cooldownMinutes: event.target.value,
                          })
                        }
                      />
                    ) : (
                      <select
                        value={form.cooldownMinutes}
                        onChange={event =>
                          event.target.value === 'custom'
                            ? setForm({ ...form, cooldownCustom: true })
                            : setForm({
                                ...form,
                                cooldownMinutes: event.target.value,
                              })
                        }
                      >
                        {COOLDOWN_PRESETS.map(minutes => (
                          <option key={minutes} value={minutes}>
                            {minutes} minuto{minutes === 1 ? '' : 's'}
                          </option>
                        ))}
                        <option value="custom">Personalizado…</option>
                      </select>
                    )}
                  </label>
                </div>
              </div>
            </section>

            <section className="rule-form__block">
              <div className="rule-form__blockHead">
                <span className="rule-form__number">03</span>
                <div>
                  <h2 className="section-title">A quién avisar</h2>
                  <p className="relevance__note">Define quién debe recibir esta alerta vía correo electrónico.</p>
                </div>
              </div>

              <div className="rule-form__field">
                <span>Notificar por correo electrónico</span>
                <ChipInput values={form.notifyEmails} onChange={notifyEmails => setForm({ ...form, notifyEmails })} placeholder="nombre@dominio.com" />
                <small>Presiona coma o Enter para separar cada correo. Déjalo vacío para verlo solo en el tablero.</small>
              </div>
            </section>

            <footer className="rule-form__actions">
              <Button variant="ghost" onClick={() => navigate('/alertas')}>
                Cancelar
              </Button>
              <Button variant="primary" busy={busy} disabled={!canSave} onClick={save}>
                {rule ? 'Guardar cambios' : 'Crear regla'}
              </Button>
            </footer>
          </div>
        </div>

        <RuleSummary form={form} rule={rule} cooldownMinutes={cooldownMinutes} watchesRelevance={watchesRelevance} />
      </div>
    </div>
  );
}

/** Live-updating, from the same form state the fields above edit — never a second source of truth
 *  for what the rule is about to become. */
function RuleSummary({ form, rule, cooldownMinutes, watchesRelevance }: { form: FormState; rule?: EditorialAlertRule; cooldownMinutes: number; watchesRelevance: boolean }) {
  return (
    <aside className="rule-summary">
      <header className="rule-summary__head">
        <p>Resumen de configuración</p>
        <h2>Resumen de la alerta</h2>
      </header>

      <div className="rule-summary__row">
        <small>Nombre</small>
        <strong>{form.name.trim() || 'Alerta sin nombre'}</strong>
      </div>

      <div className="rule-summary__row">
        <small>Términos</small>
        {form.keywords.length === 0 ? (
          <span className="rule-summary__empty">Aún sin términos</span>
        ) : (
          <ul className="topic-tags topic-tags--small">
            {form.keywords.map(keyword => (
              <li key={keyword} className="topic-tag">
                {keyword}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rule-summary__row">
        <small>Dónde busca</small>
        {form.surfaces.length === 0 ? <span className="rule-summary__empty">Aún sin ámbitos</span> : <strong>{formatAlertScopes(form.surfaces)}</strong>}
      </div>

      {watchesRelevance && (
        <div className="rule-summary__row">
          <small>Relevancia</small>
          <strong>{RELEVANCE_PRESETS.find(preset => preset.value === form.minRelevanceScore.trim())?.label ?? `Puntaje mínimo ${form.minRelevanceScore.trim() || ACTION_THRESHOLD}`}</strong>
        </div>
      )}

      <div className="rule-summary__row">
        <small>Silencio entre avisos</small>
        <strong>{cooldownMinutes} minutos</strong>
      </div>

      <div className="rule-summary__row">
        <small>Notificación</small>
        <strong>{form.notifyEmails.length > 0 ? form.notifyEmails.join(', ') : 'Solo en el tablero'}</strong>
      </div>

      <footer className="rule-summary__foot">
        <div>
          <small>Fecha de creación</small>
          <strong>{rule?.createdAt ? formatDate(rule.createdAt) : 'Sin dato'}</strong>
        </div>
        <div>
          <small>Última modificación</small>
          <strong>{rule?.updatedAt ? formatDate(rule.updatedAt) : 'Sin modificaciones'}</strong>
        </div>
      </footer>

      <p className="rule-summary__ready">{rule ? 'Los cambios se aplicarán a esta alerta cuando los guardes.' : 'La alerta comenzará a monitorear cuando completes y guardes esta configuración.'}</p>
    </aside>
  );
}

/**
 * Comma, Enter or Tab converts the current text into a chip. Backspace on an empty input removes
 * the last one. The server normalizes and dedupes again, so this stays a plain list of strings —
 * no client-side validation to keep in sync with the real one.
 */
function ChipInput({ values, onChange, placeholder }: { values: Array<string>; onChange: (values: Array<string>) => void; placeholder: string }) {
  const [draft, setDraft] = useState('');

  function commit() {
    const value = draft.trim();
    setDraft('');
    if (value !== '' && !values.includes(value)) onChange([...values, value]);
  }

  return (
    <div className="chip-input">
      {values.map(value => (
        <span key={value} className="chip-input__chip">
          {value}
          <button type="button" aria-label={`Quitar ${value}`} onClick={() => onChange(values.filter(item => item !== value))}>
            ×
          </button>
        </span>
      ))}
      <input
        value={draft}
        placeholder={values.length === 0 ? placeholder : ''}
        onChange={event => setDraft(event.target.value)}
        onKeyDown={event => {
          if (event.key === ',' || event.key === 'Enter' || event.key === 'Tab') {
            if (draft.trim() !== '') {
              event.preventDefault();
              commit();
            }
          } else if (event.key === 'Backspace' && draft === '' && values.length > 0) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={commit}
      />
    </div>
  );
}
